// src/pages/league-dashboard/LiveDraft.js - Live Draft Room (Refactored with Hooks)
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Wifi, WifiOff, AlertCircle, Clock, Users, X, ArrowLeft, Zap, Lightbulb, Loader
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI, authAPI } from '../../services/apiService';
import notificationService from '../../services/notificationService';
import PlayerLink from '../../components/PlayerLink';
import LeagueBanner from '../../components/league-dashboard/LeagueBanner';

// Draft components
import SimplifiedPicksTable from '../../components/draft/SimplifiedPicksTable';
import VirtualAvailablePlayersTable from './live-draft/components/VirtualAvailablePlayersTable';
import DraftFilters from '../../components/draft/DraftFilters';
import NotificationSettings from '../../components/draft/NotificationSettings';
import DraftChat from '../../components/draft/DraftChat';
import DraftControls from '../../components/draft/DraftControls';
import DraftTicker from '../../components/draft/DraftTicker';
import DraftNotificationBell from './live-draft/components/DraftNotificationBell';
import OnTheClockTile from '../../components/draft/OnTheClockTile';
import DraftRosterPanel from '../../components/draft/DraftRosterPanel';
import DraftQueuePanel from '../../components/draft/DraftQueuePanel';
import PositionAssignmentDropdown from '../../components/league-dashboard/PositionAssignmentDropdown';
import DraftErrorBoundary from '../../components/draft/DraftErrorBoundary';

// Custom hooks
import {
  useDraftSocket,
  useDraftState,
  useDraftTimer,
  useDraftFilters,
  useDraftUI,
  useMyTeam,
  useDraftPick,
  useDraftTrades,
  useDraftNotifications
} from './live-draft/hooks';

// Socket handlers + payload builders (extracted in Tier 2 #8 commit 8a)
import { createSocketHandlers, buildMakePickPayload } from './live-draft/utils/socketHandlers';

// Trading components
import TradeProposalModal from './live-draft/components/TradeProposalModal';
import IncomingTradeNotification from './live-draft/components/IncomingTradeNotification';
import TradeResponseModal from './live-draft/components/TradeResponseModal';
import TradeHistoryPanel from './live-draft/components/TradeHistoryPanel';

// Player Profile Modal
import PlayerProfileModal from '../../components/PlayerProfileModal';

// Post-draft summary
import DraftCompleteSummary from './live-draft/components/DraftCompleteSummary';

// Utilities
import { transformPlayerForAssignment } from './live-draft/utils/draftHelpers';
import { isPitcherPosition } from '../../utils/playerClassification';

const LiveDraft = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  
  // Track render count for debugging
  const renderCountRef = useRef(0);
  
  // Local state (component-specific, not in hooks)
  const [league, setLeague] = useState(null);
  const [availablePlayersForFilters, setAvailablePlayersForFilters] = useState([]);
  const connectionAttemptedRef = useRef(false);

  // Banner state (loaded from league settings, same source as LeagueDashboard)
  const [leagueBanner, setLeagueBanner] = useState(null);
  const [bannerConfig, setBannerConfig] = useState({
    height: 120,
    titleSize: 'text-3xl',
    titlePosition: 'left',
    titleStyle: 'font-bold'
  });
  
  // Trading UI state (must be before any conditional returns)
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [showTradeResponseModal, setShowTradeResponseModal] = useState(false);
  const [selectedTradeForResponse, setSelectedTradeForResponse] = useState(null);

  // Player queue state
  const [playerQueue, setPlayerQueue] = useState([]);

  // Suggest pick panel
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Draggable panel widths (persisted to localStorage)
  const [leftWidth, setLeftWidth] = useState(() =>
    parseInt(localStorage.getItem('draft-left-width')) || 224
  );
  const [rightWidth, setRightWidth] = useState(() =>
    parseInt(localStorage.getItem('draft-right-width')) || 320
  );
  const isDraggingRef = useRef(null); // 'left' | 'right' | null
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);

  // Trade preselection for "Offer Trade" from roster panel
  const [tradePreselection, setTradePreselection] = useState(null);

  // Player profile modal state
  const [profileModalPlayer, setProfileModalPlayer] = useState(null);

  // Fetch league details ref (must be before any conditional returns)
  const leagueFetchedRef = useRef(false);

  // Get draft year from league (needed for socket connection)
  // Use useMemo to ensure stable value and prevent hook re-initialization
  const draftYear = useMemo(() => {
    if (league && (league.draft_year || league.current_season)) {
      return league.draft_year || league.current_season;
    }
    return null;
  }, [league?.draft_year, league?.current_season]);

  // Initialize hooks - only if we have a valid draftYear
  const socketHook = useDraftSocket(leagueId, draftYear);
  const stateHook = useDraftState(socketHook.socket, leagueId);
  const timerHook = useDraftTimer(socketHook.socket);
  const filtersHook = useDraftFilters();
  const uiHook = useDraftUI();
  const teamHook = useMyTeam(stateHook.draftState, leagueId);
  const standardContractLength = league?.salary_settings?.standard_contract_length;
  const pickHook = useDraftPick(
    socketHook.socket,
    stateHook.draftState,
    teamHook.myTeam,
    leagueId,
    teamHook.isCommissioner,
    stateHook.draftedPlayerIds,
    standardContractLength
  );
  const tradesHook = useDraftTrades(socketHook.socket, teamHook.myTeam, leagueId);
  const notifHook = useDraftNotifications(socketHook.socket, teamHook.myTeam?.team_id);
  
  // NUCLEAR: Lock scroll at top during initial load, release after everything is rendered
  useEffect(() => {
    const preventScroll = () => window.scrollTo(0, 0);
    window.addEventListener('scroll', preventScroll);

    const timer = setTimeout(() => {
      window.removeEventListener('scroll', preventScroll);
    }, 3000);

    return () => {
      window.removeEventListener('scroll', preventScroll);
      clearTimeout(timer);
    };
  }, []);

  // Draggable panel resize handlers
  const startDrag = useCallback((side, e) => {
    isDraggingRef.current = side;
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = side === 'left' ? leftWidth : rightWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [leftWidth, rightWidth]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const delta = e.clientX - dragStartXRef.current;

      if (isDraggingRef.current === 'left') {
        const newWidth = Math.min(400, Math.max(180, dragStartWidthRef.current + delta));
        setLeftWidth(newWidth);
      } else {
        // Right panel: dragging LEFT makes it wider
        const newWidth = Math.min(500, Math.max(250, dragStartWidthRef.current - delta));
        setRightWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem('draft-left-width', String(leftWidth));
      localStorage.setItem('draft-right-width', String(rightWidth));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [leftWidth, rightWidth]);

  // Request notification permission on mount
  useEffect(() => {
    notificationService.requestPermission();
  }, []);

  // Store setError in ref to avoid dependency issues
  const setErrorRef = useRef(stateHook.setError);
  useEffect(() => {
    setErrorRef.current = stateHook.setError;
  }, [stateHook.setError]);

  useEffect(() => {
    if (leagueFetchedRef.current) return;
    leagueFetchedRef.current = true;

    const fetchLeague = async () => {
      try {
        const leagueResponse = await leaguesAPI.getLeagueDetails(leagueId);
        if (leagueResponse.success && leagueResponse.league) {
          setLeague(leagueResponse.league);
        }
        // Load banner config from league settings (same source as LeagueDashboard)
        const settingsResponse = await leaguesAPI.getLeagueSettings(leagueId);
        if (settingsResponse?.success && settingsResponse.settings) {
          if (settingsResponse.settings.league_banner_url) {
            setLeagueBanner(settingsResponse.settings.league_banner_url);
          }
          if (settingsResponse.settings.banner_config) {
            const config = settingsResponse.settings.banner_config;
            setBannerConfig({
              height: config.height || 120,
              titleSize: config.titleSize || 'text-3xl',
              titlePosition: config.titlePosition || 'left',
              titleStyle: config.titleStyle || 'font-bold'
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch league details:', err);
        setErrorRef.current('Failed to load league details');
        leagueFetchedRef.current = false; // Allow retry on error
      }
    };
    fetchLeague();
  }, [leagueId]);
  
  // Reset connection attempt flag when leagueId changes (navigating to different league)
  useEffect(() => {
    connectionAttemptedRef.current = false;
  }, [leagueId]);

  // Connect to socket once we have league data
  useEffect(() => {
    // Only attempt connection once when draftYear becomes available
    if (!draftYear || connectionAttemptedRef.current || socketHook.socket) {
      return;
    }

    connectionAttemptedRef.current = true;
    console.log('[LiveDraft] Initiating socket connection for league:', leagueId);
    try {
      socketHook.connect();
    } catch (err) {
      console.error('Error connecting socket:', err);
      connectionAttemptedRef.current = false; // Allow retry on error
    }
    // Only depend on draftYear - don't depend on socket or connect to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftYear]);

  // Refs for socket event handlers - declared before use
  const setTimerDataRef = useRef(timerHook.setTimerData);
  useEffect(() => {
    setTimerDataRef.current = timerHook.setTimerData;
  }, [timerHook.setTimerData]);

  // Set up additional socket event handlers for notifications
  // Store myTeam.team_id in ref to avoid recreating handlers on team change
  const myTeamIdRef = useRef(teamHook.myTeam?.team_id);
  useEffect(() => {
    myTeamIdRef.current = teamHook.myTeam?.team_id;
  }, [teamHook.myTeam?.team_id]);

  useEffect(() => {
    if (!socketHook.socket) return;

    const {
      handlePickMade,
      handleYourTurn,
      handleTimerExpired,
      handleDraftStarted,
      handleDraftComplete,
      handlePicksTraded,
    } = createSocketHandlers({ myTeamIdRef, setTimerDataRef });

    socketHook.socket.on('pick-made', handlePickMade);
    socketHook.socket.on('your-turn', handleYourTurn);
    socketHook.socket.on('timer-expired', handleTimerExpired);
    socketHook.socket.on('draft-started', handleDraftStarted);
    socketHook.socket.on('draft-complete', handleDraftComplete);
    socketHook.socket.on('picks-traded', handlePicksTraded);

    return () => {
      socketHook.socket.off('pick-made', handlePickMade);
      socketHook.socket.off('your-turn', handleYourTurn);
      socketHook.socket.off('timer-expired', handleTimerExpired);
      socketHook.socket.off('draft-started', handleDraftStarted);
      socketHook.socket.off('draft-complete', handleDraftComplete);
      socketHook.socket.off('picks-traded', handlePicksTraded);
    };
  }, [socketHook.socket]);

  // Keep-alive: Refresh auth session every 5 minutes
  useEffect(() => {
    const KEEP_ALIVE_INTERVAL = 5 * 60 * 1000;

    const keepAlive = async () => {
      try {
        await authAPI.checkAuth();
      } catch (err) {
        console.warn('[LiveDraft] Keep-alive auth refresh failed (ignored):', err);
      }
    };

    const intervalId = setInterval(keepAlive, KEEP_ALIVE_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  // Initialize timer data from draft state (for clients joining mid-draft)
  useEffect(() => {
    if (stateHook.draftState?.timer?.expires_at) {
      setTimerDataRef.current({
        started_at: stateHook.draftState.timer.started_at,
        expires_at: stateHook.draftState.timer.expires_at,
        duration_seconds: stateHook.draftState.timer.duration_seconds
      });
    }
  }, [stateHook.draftState?.timer]);

  // Computed values
  const isDraftInProgress = stateHook.isInProgress;
  const isYourTurnNow = isDraftInProgress && teamHook.isMyTurn;
  const canCommissionerPick = teamHook.isCommissioner && isDraftInProgress && !stateHook.isPaused && !teamHook.isMyTurn;

  // Derive roster from draft picks for PositionAssignmentDropdown
  // When commissioner picks for another team, use that team's roster for slot availability
  const assignmentTeamId = canCommissionerPick
    ? stateHook.draftState?.currentPick?.current_owner_team_id
    : teamHook.myTeam?.team_id;

  // Fetch the target team's full roster for position assignment (keepers + drafted)
  const [targetTeamRoster, setTargetTeamRoster] = useState([]);
  useEffect(() => {
    if (!assignmentTeamId || !leagueId) return;
    const fetchRoster = async () => {
      try {
        const response = await leaguesAPI.getTeamRosterCanonical(leagueId, assignmentTeamId);
        setTargetTeamRoster(response?.players || response?.roster || (Array.isArray(response) ? response : []));
      } catch (err) {
        console.error('[LiveDraft] Failed to fetch target team roster:', err);
      }
    };
    fetchRoster();
  }, [assignmentTeamId, leagueId, stateHook.draftState?.picksMade]);

  const userRosterForAssignment = useMemo(() => {
    // Use the DB roster as the base (has keepers + all current players for the target team)
    const dbPlayers = targetTeamRoster.map(p => ({ ...p }));
    const dbIds = new Set(dbPlayers.map(p => p.ids?.league_player).filter(Boolean));

    // Add draft picks not yet in DB (very recently drafted, DB might not have them yet)
    const draftPicked = (stateHook.draftState?.picks || [])
      .filter(pick =>
        pick.current_owner_team_id === assignmentTeamId &&
        pick.league_player_id &&
        !dbIds.has(pick.league_player_id)
      )
      .map(pick => ({
        ids: { league_player: pick.league_player_id },
        info: {
          full_name: pick.player?.info?.full_name,
          position: pick.player?.info?.position,
          mlb_team: pick.player?.info?.mlb_team
        },
        league_context: {
          roster: {
            status: pick.roster_status || 'active',
            position: pick.roster_position
          }
        }
      }));

    return [...dbPlayers, ...draftPicked];
  }, [stateHook.draftState?.picks, assignmentTeamId, targetTeamRoster]);

  // Transform selected player for PositionAssignmentDropdown
  const playerForAssignment = useMemo(() => {
    return transformPlayerForAssignment(pickHook.selectedPlayer);
  }, [pickHook.selectedPlayer]);

  // Handle position assignment from modal
  const handlePositionAssignment = useCallback(async (assignmentData) => {
    // Capture current values to avoid stale closures
    const selectedPlayer = pickHook.selectedPlayer;
    const isProcessing = pickHook.isProcessing;
    const draftState = stateHook.draftState;
    const myTeam = teamHook.myTeam;

    if (!selectedPlayer || isProcessing || !draftState?.currentPick || !myTeam) return;

    // Store previous state for potential rollback
    stateHook.storePreviousState();

    // Apply optimistic update
    const currentPickId = draftState.currentPick.pick_id;
    const player = selectedPlayer;
    const leaguePlayerId = player.ids?.league_player;

    stateHook.setDraftState(prev => {
      if (!prev) return prev;
      const updated = { ...prev, picks: [...prev.picks] };
      const pickIndex = updated.picks.findIndex(p => p.pick_id === currentPickId);
      if (pickIndex !== -1) {
        updated.picks[pickIndex] = {
          ...updated.picks[pickIndex],
          league_player_id: leaguePlayerId,
          picked_at: new Date().toISOString(),
          player: {
            league_player_id: leaguePlayerId,
            info: {
              full_name: player.info?.full_name,
              position: player.info?.position,
              mlb_team: player.info?.mlb_team
            },
            financial: {
              baseline_price: player.league_context?.financial?.baseline_price
            }
          }
        };
        const nextPick = updated.picks[pickIndex + 1] || null;
        updated.currentPick = nextPick;
        updated.currentPickIndex = nextPick ? pickIndex + 1 : updated.picks.length;
      }
      return updated;
    });

    // Close modal and make pick via hook
    uiHook.closePositionModal();
    await pickHook.makePick(player, assignmentData);
  }, [
    pickHook.selectedPlayer,
    pickHook.isProcessing,
    pickHook.makePick,
    stateHook.draftState,
    stateHook.setDraftState,
    stateHook.storePreviousState,
    teamHook.myTeam,
    uiHook.closePositionModal
  ]);

  // Handle slot click from roster panel
  const handleRosterSlotClick = useCallback((slotId, slotPosition) => {
    if (!pickHook.selectedPlayer || !isYourTurnNow) return;

    const rosterStatus = slotPosition === 'bench' ? 'bench' :
                         slotPosition === 'minors' ? 'minors' : 'active';
    const rosterPosition = rosterStatus === 'active' ? slotId : null;

    uiHook.setPendingRosterAssignment({ roster_status: rosterStatus, roster_position: rosterPosition });
    uiHook.setShowPositionModal(true);
  }, [pickHook.selectedPlayer, isYourTurnNow, uiHook]);

  // Handle "Offer Trade" from roster panel viewing another team
  const handleOfferTradeFromRoster = useCallback((teamId, playerId) => {
    setTradePreselection({ teamId, playerId });
    setShowTradeModal(true);
  }, []);

  // Handle "Draft to Position" button click
  const handleDraftButtonClick = useCallback(() => {
    if (!pickHook.selectedPlayer || !(isYourTurnNow || canCommissionerPick)) return;
    uiHook.setPendingRosterAssignment(null);
    uiHook.setShowPositionModal(true);
  }, [pickHook.selectedPlayer, isYourTurnNow, canCommissionerPick, uiHook]);

  // Draft a suggested player directly (one-click from suggestion panel)
  const handleDraftSuggestion = useCallback(async (suggestion) => {
    if (!isYourTurnNow || pickHook.isProcessing) return;
    const player = {
      ids: { league_player: suggestion.league_player_id, mlb: suggestion.player_id },
      info: { full_name: suggestion.player_name, position: suggestion.position },
      financial: { baseline_price: suggestion.price }
    };
    const slotParts = suggestion.slot?.split('_') || [];
    const rosterStatus = suggestion.slot ? 'active' : 'bench';
    const rosterPosition = suggestion.slot || null;
    setShowSuggestions(false);
    await pickHook.makePick(player, { roster_status: rosterStatus, roster_position: rosterPosition });
  }, [isYourTurnNow, pickHook]);

  // Get drafted player IDs for filtering
  // Use stateHook.draftedPlayerIds (a Set) and convert to Array for VirtualAvailablePlayersTable
  // This ensures we use the centralized logic that checks both pick.league_player_id AND pick.player_selected paths
  const draftedPlayerIds = useMemo(() => {
    return Array.from(stateHook.draftedPlayerIds || []);
  }, [stateHook.draftedPlayerIds]);

  // Combined error from socket or state
  const error = stateHook.error || socketHook.connectionError;

  // Memoized callback for players loaded
  const handlePlayersLoaded = useCallback((players) => {
    setAvailablePlayersForFilters(players);
  }, []);

  // Memoized callbacks to prevent unnecessary child re-renders
  const handleOpenTradeModal = useCallback(() => setShowTradeModal(true), []);
  const handleCloseTradeModal = useCallback(() => setShowTradeModal(false), []);

  const handleCloseTradeResponseModal = useCallback(() => {
    setShowTradeResponseModal(false);
    setSelectedTradeForResponse(null);
  }, []);

  const handleAcceptTrade = useCallback((tradeId) => {
    tradesHook.respondToTrade(tradeId, 'accept');
    setShowTradeResponseModal(false);
    setSelectedTradeForResponse(null);
  }, [tradesHook]);

  const handleRejectTrade = useCallback((tradeId) => {
    tradesHook.respondToTrade(tradeId, 'reject');
    setShowTradeResponseModal(false);
    setSelectedTradeForResponse(null);
  }, [tradesHook]);

  // Close trade modal on successful proposal
  useEffect(() => {
    if (tradesHook.tradeSuccess) {
      setShowTradeModal(false);
      setTradePreselection(null);
    }
  }, [tradesHook.tradeSuccess]);

  // Purge drafted players from the queue automatically
  useEffect(() => {
    if (!stateHook.draftedPlayerIds || stateHook.draftedPlayerIds.size === 0) return;
    setPlayerQueue(prev => {
      const filtered = prev.filter(p => {
        const pid = p?.ids?.league_player || p?.league_player_id;
        return pid && !stateHook.draftedPlayerIds.has(pid);
      });
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [stateHook.draftedPlayerIds]);

  // Find the first vacant roster slot for a player, matching PositionAssignmentDropdown logic.
  // Returns { roster_status, roster_position } or null if no slot available.
  const findVacantSlot = useCallback((player) => {
    const posReqs = league?.roster_settings?.position_requirements;
    if (!posReqs) return null;

    const playerPosition = player?.info?.position;
    const eligiblePositions = player?.league_context?.roster?.effective_eligibility ||
                              (playerPosition ? [playerPosition] : []);

    const isEligible = (pos) => {
      if (eligiblePositions.includes(pos)) return true;
      if (pos === 'MI' && (eligiblePositions.includes('2B') || eligiblePositions.includes('SS'))) return true;
      if (pos === 'CI' && (eligiblePositions.includes('1B') || eligiblePositions.includes('3B'))) return true;
      if (pos === 'UTIL' && !eligiblePositions.some(isPitcherPosition)) return true;
      if (pos === 'P' && ['SP', 'RP'].some(p => eligiblePositions.includes(p))) return true;
      if (pos === 'OF' && ['LF', 'CF', 'RF', 'OF'].some(p => eligiblePositions.includes(p))) return true;
      return false;
    };

    // Build set of occupied slots from current roster
    const occupiedSlots = new Set();
    userRosterForAssignment.forEach(rp => {
      const status = rp.league_context?.roster?.status;
      const position = rp.league_context?.roster?.position;
      if (status === 'active' && position) occupiedSlots.add(position);
    });

    // Pass 1: Natural position slots first, then composite (MI/CI/UTIL/P/OF)
    const naturalPositions = [];
    const compositePositions = [];
    Object.entries(posReqs).forEach(([pos, config]) => {
      if (!isEligible(pos)) return;
      if (['MI', 'CI', 'UTIL', 'P', 'OF'].includes(pos) && !eligiblePositions.includes(pos)) {
        compositePositions.push([pos, config]);
      } else {
        naturalPositions.push([pos, config]);
      }
    });

    for (const [pos, config] of [...naturalPositions, ...compositePositions]) {
      const maxSlots = config?.slots || 0;
      for (let i = 0; i < maxSlots; i++) {
        const slotId = `${pos}_${i}`;
        if (!occupiedSlots.has(slotId)) {
          return { roster_status: 'active', roster_position: slotId };
        }
      }
    }

    return null; // No vacant active slot
  }, [league?.roster_settings?.position_requirements, userRosterForAssignment]);

  // Auto-pick: fires immediately when it's your turn and auto-pick is enabled.
  // Uses currentPick.pick_id to detect new picks (handles back-to-back turns
  // where isYourTurnNow stays true but pick_id changes).
  // Finds vacant roster slot and emits make-pick directly — fully automatic.
  // If no slot fits, skips queue/selected player and falls through to server auto-pick.
  // Priority: selected player > queue player > server auto-pick
  const autoPickFiredForPickRef = useRef(null);

  useEffect(() => {
    if (!isYourTurnNow || !pickHook.autoPickEnabled) return;

    const currentPickId = stateHook.draftState?.currentPick?.pick_id;
    if (!currentPickId) return;

    // Already fired for this exact pick — don't re-fire
    if (autoPickFiredForPickRef.current === currentPickId) return;
    autoPickFiredForPickRef.current = currentPickId;

    const socket = socketHook.socket;
    const currentPick = stateHook.draftState?.currentPick;
    if (!socket || !currentPick) return;
    if (!standardContractLength) return;

    // Priority 1: Selected player — find slot and draft immediately
    if (pickHook.selectedPlayer) {
      const playerId = pickHook.selectedPlayer.ids?.league_player;
      if (playerId && !stateHook.draftedPlayerIds?.has(playerId)) {
        const slot = findVacantSlot(pickHook.selectedPlayer);
        if (slot) {
          socket.emit('make-pick', buildMakePickPayload({
            pickId: currentPick.pick_id,
            leaguePlayerId: playerId,
            contractYears: standardContractLength,
            slot,
          }));
          pickHook.deselectPlayer();
          return;
        }
        // No slot fits — fall through to server auto-pick
      }
    }

    // Priority 2: Queue — find first player with a vacant slot
    if (playerQueue.length > 0) {
      for (let i = 0; i < playerQueue.length; i++) {
        const queuePlayer = playerQueue[i];
        const pid = queuePlayer?.ids?.league_player || queuePlayer?.league_player_id;
        if (!pid || stateHook.draftedPlayerIds?.has(pid)) continue;

        const slot = findVacantSlot(queuePlayer);
        if (slot) {
          socket.emit('make-pick', buildMakePickPayload({
            pickId: currentPick.pick_id,
            leaguePlayerId: pid,
            contractYears: standardContractLength,
            slot,
          }));
          setPlayerQueue(prev => prev.filter((_, idx) => idx !== i));
          return;
        }
        // This queue player doesn't fit — try next one
      }
      // No queue player fits — fall through to server auto-pick
    }

    // Priority 3: Server auto-pick (best available with its own slot logic)
    pickHook.requestAutoPick();
  }, [isYourTurnNow, pickHook.autoPickEnabled, stateHook.draftState?.currentPick?.pick_id, pickHook.selectedPlayer, playerQueue, pickHook.requestAutoPick, pickHook.deselectPlayer, socketHook.socket, stateHook.draftState?.currentPick, stateHook.draftedPlayerIds, findVacantSlot, standardContractLength]);

  const handlePickClick = useCallback((pick) => {
    if (pick.league_player_id) {
      uiHook.setSelectedPlayerForModal(pick);
    }
  }, [uiHook]);

  const handleViewIncomingTrade = useCallback((trade) => {
    setSelectedTradeForResponse(trade);
    setShowTradeResponseModal(true);
  }, []);

  // Player profile modal handler
  const handleViewPlayerProfile = useCallback((player) => {
    setProfileModalPlayer(player);
  }, []);

  // Handler for clicking player name in draft picks table
  const handleDraftPickPlayerClick = useCallback((pick) => {
    if (pick.player) {
      setProfileModalPlayer(pick.player);
    }
  }, []);

  // Queue handlers
  const handleAddToQueue = useCallback((player) => {
    const playerId = player?.ids?.league_player || player?.league_player_id;
    if (!playerId) return;

    // Don't add duplicates
    setPlayerQueue(prev => {
      const exists = prev.some(p =>
        (p?.ids?.league_player || p?.league_player_id) === playerId
      );
      if (exists) return prev;
      return [...prev, player];
    });
  }, []);

  const handleRemoveFromQueue = useCallback((index) => {
    setPlayerQueue(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleMoveQueueUp = useCallback((index) => {
    if (index <= 0) return;
    setPlayerQueue(prev => {
      const newQueue = [...prev];
      [newQueue[index - 1], newQueue[index]] = [newQueue[index], newQueue[index - 1]];
      return newQueue;
    });
  }, []);

  const handleMoveQueueDown = useCallback((index) => {
    setPlayerQueue(prev => {
      if (index >= prev.length - 1) return prev;
      const newQueue = [...prev];
      [newQueue[index], newQueue[index + 1]] = [newQueue[index + 1], newQueue[index]];
      return newQueue;
    });
  }, []);

  const handleClearQueue = useCallback(() => {
    setPlayerQueue([]);
  }, []);

  const handleDraftOrderGenerated = useCallback(() => {
    if (socketHook.socket && league) {
      socketHook.socket.emit('reload-draft', { league_id: leagueId, year: draftYear });
    }
  }, [socketHook.socket, league, leagueId, draftYear]);

  // Memoized derived data for trade modals
  const allTeamsArray = useMemo(() =>
    Array.from(teamHook.teamsMap?.values() || []),
    [teamHook.teamsMap]
  );

  // My tradeable players: keepers + newly drafted (excluding keepers already in roster)
  const myTradePlayers = useMemo(() => {
    const keptIds = new Set((teamHook.keptPlayers || []).map(p => p.ids?.league_player).filter(Boolean));
    const newDrafted = (teamHook.myRoster || []).filter(p => !keptIds.has(p.ids?.league_player));
    return [...(teamHook.keptPlayers || []), ...newDrafted];
  }, [teamHook.keptPlayers, teamHook.myRoster]);

  const fromTeamForResponse = useMemo(() =>
    selectedTradeForResponse ?
      allTeamsArray.find(t => t.team_id === selectedTradeForResponse.proposing_team_id)
      : null,
    [selectedTradeForResponse, allTeamsArray]
  );

  const incomingFromTeam = useMemo(() =>
    tradesHook.incomingTrade ?
      allTeamsArray.find(t => t.team_id === tradesHook.incomingTrade.from_team) || { team_name: 'Unknown Team' }
      : null,
    [tradesHook.incomingTrade, allTeamsArray]
  );

  // Track render count for debugging (log only on significant counts)
  renderCountRef.current += 1;
  if (renderCountRef.current === 1) {
    console.log('[LiveDraft] First render');
  } else if (renderCountRef.current === 50) {
    console.warn('[LiveDraft] 50 renders - checking for performance issues');
  }

  // Wait for league settings before rendering draft
  if (!league) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${dynastyTheme.classes.bg.dark}`}>
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-yellow-400 mx-auto mb-4" />
          <p className={dynastyTheme.classes.text.white}>
            Loading league settings...
          </p>
        </div>
      </div>
    );
  }

  // Render loading
  if (stateHook.loading && !stateHook.draftState) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${dynastyTheme.classes.bg.dark}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className={dynastyTheme.classes.text.white}>
            Connecting to draft...
          </p>
        </div>
      </div>
    );
  }

  // Render error
  if (error && !stateHook.draftState) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${dynastyTheme.classes.bg.dark}`}>
        <div className={`${dynastyTheme.components.card.base} max-w-md p-6`}>
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <h2 className={`${dynastyTheme.components.heading.h2} text-red-500`}>
              Error Connecting to Draft
            </h2>
          </div>
          <p className={`${dynastyTheme.classes.text.white} mb-4 whitespace-pre-wrap`}>
            {error}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className={`${dynastyTheme.components.button.primary} flex-1`}
            >
              Retry
            </button>
            <button
              onClick={() => navigate(-1)}
              className={`${dynastyTheme.components.button.secondary} flex-1`}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render draft room
  return (
    <div className={`min-h-screen ${dynastyTheme.classes.bg.dark} p-4`}>
      {/* Banner Header */}
      <div className="mb-2">
        <LeagueBanner
          leagueBanner={leagueBanner}
          title={league?.league_name || socketHook.connectionDetails?.league_name || 'League'}
          subtitle={`Live Draft — ${draftYear || ''}`}
          badge={
            isYourTurnNow ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border-2 border-yellow-500 rounded-lg animate-pulse backdrop-blur-sm">
                <Clock className="w-5 h-5 text-yellow-500" />
                <span className="font-bold text-yellow-500">YOUR TURN!</span>
              </div>
            ) : null
          }
          centerContent={
            teamHook.isCommissioner ? (
              <DraftControls
                leagueId={leagueId}
                year={draftYear}
                socket={socketHook.socket}
                draftState={stateHook.draftState}
                userIsCommissioner={teamHook.isCommissioner}
                onDraftOrderGenerated={handleDraftOrderGenerated}
                inline={true}
              />
            ) : null
          }
          actions={
            <div className="flex items-center gap-3">
              {socketHook.connected ? (
                <Wifi className="w-4 h-4 text-green-400" />
              ) : (
                <WifiOff className="w-4 h-4 text-red-400" />
              )}
              <span className="text-sm text-white" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>
                {socketHook.connected ? 'Connected' : 'Disconnected'}
              </span>
              {stateHook.draftState && (
                <span className="text-sm text-gray-300" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>
                  {stateHook.draftState.picksMade || 0} / {stateHook.draftState.totalPicks || 0} picks
                </span>
              )}
              <DraftNotificationBell
                notifications={notifHook.notifications}
                unreadCount={notifHook.unreadCount}
                markRead={notifHook.markRead}
                markAllRead={notifHook.markAllRead}
              />
            </div>
          }
          minHeight="160px"
          bannerConfig={bannerConfig}
        />
        <button
          onClick={() => navigate(`/leagues/${leagueId}`)}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 mt-1 mb-2 ml-1"
        >
          <ArrowLeft className="w-3 h-3" /> Back to Dashboard
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className={`${dynastyTheme.components.card.base} mb-4 p-4 bg-red-900/20 border-2 border-red-500`}>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className={dynastyTheme.classes.text.white}>{error}</span>
            <button
              onClick={() => stateHook.setError(null)}
              className="ml-auto"
            >
              <X className="w-5 h-5 text-red-500" />
            </button>
          </div>
        </div>
      )}

      {/* Draft Complete Summary */}
      {stateHook.isCompleted && stateHook.draftState?.picks && (
        <DraftCompleteSummary
          picks={stateHook.draftState.picks}
          completedTrades={stateHook.draftState.completedTrades || tradesHook.tradeHistory}
          leagueName={socketHook.connectionDetails?.league_name || league?.league_name || 'League'}
          year={draftYear || league?.current_season}
          allTeams={allTeamsArray}
        />
      )}

      {/* Trade success/error toast */}
      {tradesHook.tradeSuccess && (
        <div className="mb-2 px-4 py-2 rounded-lg bg-green-500/20 border border-green-500/40 text-green-300 text-sm font-medium text-center">
          {tradesHook.tradeSuccess}
        </div>
      )}
      {tradesHook.tradeError && (
        <div className="mb-2 px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 text-sm font-medium text-center">
          {tradesHook.tradeError}
        </div>
      )}

      {/* Draft Ticker */}
      {stateHook.draftState?.picks && stateHook.draftState.picks.length > 0 && (
        <DraftTicker
          picks={stateHook.draftState.picks}
          currentPickNumber={stateHook.draftState?.currentPick?.overall_pick_number || null}
          onPickClick={handlePickClick}
          userTeamId={teamHook.myTeam?.team_id}
        />
      )}

      {/* Main content - 3 Panel Layout with draggable resize */}
      <div className="flex">
        {/* LEFT PANEL - Roster */}
        <div style={{ width: leftWidth, minWidth: 180, maxWidth: 400 }} className="flex-shrink-0 space-y-4 overflow-y-auto pr-1">
          <DraftRosterPanel
            league={league}
            userTeam={teamHook.myTeam}
            draftState={stateHook.draftState}
            selectedPlayer={pickHook.selectedPlayer}
            onSlotClick={handleRosterSlotClick}
            isYourTurn={isYourTurnNow}
            timerData={isDraftInProgress ? timerHook.timerData : null}
            cashBalance={teamHook.cashBalance}
            allTeams={allTeamsArray}
            onOfferTrade={handleOfferTradeFromRoster}
          />
        </div>

        {/* Left drag handle */}
        <div
          className="w-1 cursor-col-resize hover:bg-yellow-500/50 active:bg-yellow-500 transition-colors flex-shrink-0"
          onMouseDown={(e) => startDrag('left', e)}
        />

        {/* CENTER PANEL - Available Players & Selected Player */}
        <div className="flex-1 space-y-4 px-2 min-w-0">
          {/* Filters + Waiting Tile Row */}
          <div className="flex gap-4 items-stretch">
            {/* Filters Tile - 50% width */}
            <div className="w-1/2">
              <DraftFilters
                players={availablePlayersForFilters}
                filters={filtersHook.filters}
                onFiltersChange={filtersHook.setFilters}
              />
            </div>

            {/* Status + Selected Player Tile - 50% width, FIXED HEIGHT, no resize */}
            <div className="w-1/2 relative">
              <div className={`${dynastyTheme.components.card.base} p-3 h-full overflow-hidden flex flex-col ${isYourTurnNow ? 'border-2 border-yellow-500 bg-yellow-500/10' : ''}`}>
                {/* Row 1: Turn status + Auto-pick on same line */}
                <div className="flex items-center justify-between mb-1.5 flex-shrink-0">
                  <div className="flex items-center gap-1.5">
                    <Clock className={`w-3.5 h-3.5 ${isYourTurnNow ? 'text-yellow-500' : canCommissionerPick ? 'text-orange-400' : dynastyTheme.classes.text.neutralDark}`} />
                    <span className={`text-xs font-bold ${isYourTurnNow ? 'text-yellow-500' : canCommissionerPick ? 'text-orange-400' : stateHook.isPaused ? 'text-yellow-500' : dynastyTheme.classes.text.neutralLight}`}>
                      {isYourTurnNow
                        ? 'YOUR TURN TO PICK!'
                        : canCommissionerPick
                          ? `Pick for: ${stateHook.draftState.currentPick.current_owner?.team_name || 'team'}`
                          : stateHook.isPaused
                            ? 'Draft Paused'
                            : stateHook.draftState?.currentPick
                              ? `Waiting: ${stateHook.draftState.currentPick.current_owner?.team_name || 'other team'}`
                              : 'Draft not started'
                      }
                    </span>
                  </div>
                  <label className="flex items-center gap-1 cursor-pointer select-none flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={pickHook.autoPickEnabled}
                      onChange={pickHook.toggleAutoPick}
                      className="w-3 h-3 rounded accent-yellow-500"
                    />
                    <Zap className={`w-3 h-3 ${pickHook.autoPickEnabled ? 'text-yellow-400' : dynastyTheme.classes.text.neutralDark}`} />
                    <span className={`text-xs ${pickHook.autoPickEnabled ? 'text-yellow-400 font-semibold' : dynastyTheme.classes.text.neutralLight}`}>
                      Auto-pick
                    </span>
                    {pickHook.autoPickEnabled && !isYourTurnNow && (
                      <span className={`text-xs ${dynastyTheme.classes.text.neutralLight} italic ml-1`}>Armed</span>
                    )}
                  </label>
                </div>

                {/* Row 2: Suggest Pick button — always visible so users can plan ahead */}
                <div className="mb-1.5 flex-shrink-0">
                  <button
                    onClick={() => {
                      if (showSuggestions) {
                        setShowSuggestions(false);
                      } else {
                        pickHook.getSuggestions();
                        setShowSuggestions(true);
                      }
                    }}
                    disabled={pickHook.loadingSuggestions}
                    className="flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold bg-yellow-500/15 text-yellow-400 hover:bg-yellow-500/25 border border-yellow-500/30 transition-colors"
                  >
                    {pickHook.loadingSuggestions ? (
                      <Loader className="w-3 h-3 animate-spin" />
                    ) : (
                      <Lightbulb className="w-3 h-3" />
                    )}
                    {showSuggestions ? 'Hide Suggestions' : 'Suggest Pick'}
                  </button>
                </div>

                {/* Selected Player — centered in remaining space, Draft button pinned bottom-right */}
                <div className="flex-1 flex flex-col min-h-0 relative">
                  {pickHook.selectedPlayer ? (
                    <>
                      <div className="flex items-center gap-2.5 justify-center flex-1">
                        <span className={`text-sm ${dynastyTheme.classes.text.primary} font-bold tracking-wide`}>SELECTED:</span>
                        <span className={`px-2 py-1 rounded text-sm font-bold ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black}`}>
                          {pickHook.selectedPlayer?.info?.position || '-'}
                        </span>
                        <span className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>
                          {pickHook.selectedPlayer?.info?.full_name || 'Unknown'}
                        </span>
                        <span className={dynastyTheme.classes.text.neutralLight + ' text-sm'}>
                          {pickHook.selectedPlayer?.info?.mlb_team || ''}
                        </span>
                        <span className={`font-bold ${dynastyTheme.classes.text.success} text-lg`}>
                          ${Math.round(pickHook.selectedPlayer?.financial?.baseline_price || 0)}
                        </span>
                        <button
                          onClick={pickHook.deselectPlayer}
                          className={`${dynastyTheme.classes.text.neutralLight} hover:text-red-400 ml-1`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {(isYourTurnNow || canCommissionerPick) && (
                        <button
                          onClick={handleDraftButtonClick}
                          disabled={pickHook.isProcessing}
                          className={`absolute bottom-1 right-1 px-4 py-1 rounded text-xs font-bold
                            ${pickHook.isProcessing
                              ? 'bg-neutral-700 text-neutral-400 cursor-not-allowed'
                              : canCommissionerPick && !isYourTurnNow
                                ? 'bg-orange-600 hover:bg-orange-500 text-white'
                                : dynastyTheme.components.button.primary
                            }`}
                        >
                          {pickHook.isProcessing ? 'Drafting...' : canCommissionerPick && !isYourTurnNow ? 'Draft For Team' : 'Draft Player'}
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <span className={`${dynastyTheme.classes.text.neutralLight} text-xs`}>
                        {isYourTurnNow ? 'Select a player from table' : 'Click a player to preview'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Suggestions flyout — absolute positioned, overlays content below */}
              {showSuggestions && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1">
                  <div className={`${dynastyTheme.components.card.base} p-2 border-2 border-yellow-500/40 shadow-xl space-y-1.5`}>
                    {pickHook.loadingSuggestions ? (
                      <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} text-center py-3`}>Loading suggestions...</div>
                    ) : pickHook.suggestions.length === 0 ? (
                      <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} text-center py-3`}>No suggestions available.</div>
                    ) : (
                      pickHook.suggestions.map((s, idx) => {
                        // Look up full player data from loaded available players for stats
                        const fullPlayer = availablePlayersForFilters.find(p =>
                          p?.ids?.league_player === s.league_player_id || p?.ids?.mlb === s.player_id
                        );
                        // Use previous season stats when current season has no data (e.g. 2026 pre-season → show 2025)
                        const currentSeason = league?.current_season;
                        const currentHitting = fullPlayer?.stats?.season?.hitting;
                        const currentPitching = fullPlayer?.stats?.season?.pitching;
                        const hasCurrentStats = (currentHitting?.games_played || 0) > 0 || (currentPitching?.games || currentPitching?.games_played || 0) > 0;
                        const prevKey = currentSeason ? `season_${currentSeason - 1}` : null;
                        const hitting = hasCurrentStats ? currentHitting : (prevKey ? fullPlayer?.stats?.[prevKey]?.hitting : currentHitting);
                        const pitching = hasCurrentStats ? currentPitching : (prevKey ? fullPlayer?.stats?.[prevKey]?.pitching : currentPitching);
                        const isPitcher = isPitcherPosition(s.position);
                        return (
                        <div key={s.league_player_id || idx} className="flex items-center justify-between gap-2 p-2 rounded bg-neutral-800/60 border border-neutral-700/50">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className={`px-1 py-0.5 rounded text-xs font-bold ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black}`}>
                                {s.position}
                              </span>
                              <span className={`text-xs font-semibold ${dynastyTheme.classes.text.white} truncate`}>{s.player_name}</span>
                              <span className={`text-xs font-bold ${dynastyTheme.classes.text.success}`}>${Math.round(s.price || 0)}</span>
                            </div>
                            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-0.5`}>
                              {s.reason} &rarr; {s.slot ? s.slot.replace(/_\d+$/, '').replace('UTIL', 'Utility') : 'Bench'}
                            </div>
                            {fullPlayer && (
                              <div className="flex gap-2 mt-1 text-xs text-neutral-400">
                                {!isPitcher && hitting ? (
                                  <>
                                    <span>AVG {hitting.batting_avg?.toFixed(3) ?? '-'}</span>
                                    <span>HR {hitting.home_runs ?? '-'}</span>
                                    <span>RBI {hitting.runs_batted_in ?? '-'}</span>
                                    <span>SB {hitting.stolen_bases ?? '-'}</span>
                                    <span>OPS {hitting.ops?.toFixed(3) ?? '-'}</span>
                                  </>
                                ) : isPitcher && pitching ? (
                                  <>
                                    <span>ERA {pitching.era?.toFixed(2) ?? '-'}</span>
                                    <span>W {pitching.wins ?? '-'}</span>
                                    <span>SO {pitching.strikeouts ?? '-'}</span>
                                    <span>WHIP {pitching.whip?.toFixed(2) ?? '-'}</span>
                                    <span>SV {pitching.saves ?? '-'}</span>
                                  </>
                                ) : null}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleDraftSuggestion(s)}
                            disabled={!isYourTurnNow || pickHook.isProcessing}
                            className={`flex-shrink-0 px-2 py-1 rounded text-xs font-bold border transition-colors whitespace-nowrap ${
                              !isYourTurnNow || pickHook.isProcessing
                                ? 'bg-neutral-700/30 text-neutral-500 border-neutral-600/40 cursor-not-allowed'
                                : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border-yellow-500/40'
                            }`}
                          >
                            Draft
                          </button>
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Available Players Table */}
          <div className={`${dynastyTheme.components.card.base} p-4`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>
                Available Players
              </h2>
              {isYourTurnNow && (
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border-2 border-yellow-500 rounded-lg">
                  <Clock className="w-4 h-4 text-yellow-500" />
                  <span className="font-bold text-yellow-500 text-sm">YOUR TURN!</span>
                </div>
              )}
            </div>

            <VirtualAvailablePlayersTable
              leagueId={leagueId}
              filters={filtersHook.filters}
              onPlayerSelect={pickHook.selectPlayer}
              onViewProfile={handleViewPlayerProfile}
              onAddToQueue={handleAddToQueue}
              draftedPlayerIds={draftedPlayerIds}
              onPlayersLoaded={handlePlayersLoaded}
              currentSeason={league?.current_season}
              league={league}
              isYourTurn={isYourTurnNow}
              selectedPlayerId={pickHook.selectedPlayer?.ids?.league_player || pickHook.selectedPlayer?.league_player_id}
              refreshTrigger={stateHook.undoCount}
            />
          </div>

          {/* Draft Chat */}
          <DraftChat socket={socketHook.socket} userTeam={teamHook.myTeam} />
        </div>

        {/* Right drag handle */}
        <div
          className="w-1 cursor-col-resize hover:bg-yellow-500/50 active:bg-yellow-500 transition-colors flex-shrink-0"
          onMouseDown={(e) => startDrag('right', e)}
        />

        {/* RIGHT PANEL - Draft Picks & Activity */}
        <div style={{ width: rightWidth, minWidth: 250, maxWidth: 500 }} className="flex-shrink-0 space-y-4 overflow-y-auto pl-1">
          {/* On The Clock Tile */}
          <OnTheClockTile
            picks={stateHook.draftState?.picks || []}
            currentPick={(isDraftInProgress || stateHook.isPaused) ? stateHook.draftState?.currentPick : null}
            timerData={isDraftInProgress ? timerHook.timerData : null}
            isYourTurn={isYourTurnNow}
            userTeamId={teamHook.myTeam?.team_id}
            noTimer={stateHook.draftState?.noTimer || false}
            isPaused={stateHook.isPaused}
          />

          {/* Draft Picks Table */}
          <div className={`${dynastyTheme.components.card.base} p-4`}>
            <h2 className={`text-base font-bold ${dynastyTheme.classes.text.white} mb-4`}>
              Draft Picks
            </h2>

            {stateHook.draftState ? (
              <SimplifiedPicksTable
                picks={stateHook.draftState.picks || []}
                userTeamId={teamHook.myTeam?.team_id}
                onPlayerClick={handleDraftPickPlayerClick}
              />
            ) : (
              <div className="text-center py-12">
                <Users className={`w-16 h-16 mx-auto mb-4 ${dynastyTheme.classes.text.neutralDark}`} />
                <p className={dynastyTheme.classes.text.neutralLight}>
                  Loading draft picks...
                </p>
              </div>
            )}
          </div>

          {/* Player Queue */}
          <DraftQueuePanel
            queue={playerQueue}
            onRemove={handleRemoveFromQueue}
            onMoveUp={handleMoveQueueUp}
            onMoveDown={handleMoveQueueDown}
            onSelectPlayer={pickHook.selectPlayer}
            onClear={handleClearQueue}
          />

          {/* Trade History Panel */}
          <TradeHistoryPanel
            pendingTrades={tradesHook.pendingTrades}
            tradeHistory={tradesHook.tradeHistory}
            myTeam={teamHook.myTeam}
            allPicks={stateHook.draftState?.picks || []}
            allTeams={allTeamsArray}
            onCancelTrade={tradesHook.cancelTrade}
            onOpenProposal={handleOpenTradeModal}
          />

          {/* Notification Settings */}
          <NotificationSettings />
        </div>
      </div>

      {/* Position Assignment Modal */}
      <PositionAssignmentDropdown
        player={playerForAssignment}
        league={league}
        currentRoster={userRosterForAssignment}
        onAssign={handlePositionAssignment}
        onCancel={uiHook.closePositionModal}
        isVisible={uiHook.showPositionModal}
        mode="add"
      />

      {/* Trade Proposal Modal */}
      <TradeProposalModal
        isOpen={showTradeModal}
        onClose={() => { handleCloseTradeModal(); setTradePreselection(null); }}
        myTeam={teamHook.myTeam}
        myPicks={teamHook.myPicks}
        myPlayers={myTradePlayers}
        allTeams={allTeamsArray}
        allPicks={stateHook.draftState?.picks || []}
        onProposeTrade={tradesHook.proposeTrade}
        isProposing={tradesHook.isProposing}
        leagueId={leagueId}
        preselection={tradePreselection}
      />

      {/* Trade Response Modal */}
      <TradeResponseModal
        isOpen={showTradeResponseModal}
        onClose={handleCloseTradeResponseModal}
        trade={selectedTradeForResponse}
        fromTeam={fromTeamForResponse}
        myTeam={teamHook.myTeam}
        allPicks={stateHook.draftState?.picks || []}
        onAccept={handleAcceptTrade}
        onReject={handleRejectTrade}
      />

      {/* Incoming Trade Notification */}
      {tradesHook.hasIncomingTrade && tradesHook.incomingTrade && (
        <IncomingTradeNotification
          trade={tradesHook.incomingTrade.trade}
          fromTeam={incomingFromTeam}
          onView={handleViewIncomingTrade}
          onDismiss={tradesHook.dismissIncomingTrade}
        />
      )}

      {/* Player Profile Modal */}
      {profileModalPlayer && (
        <PlayerProfileModal
          playerId={profileModalPlayer?.ids?.mlb || profileModalPlayer?.mlb_player_id || profileModalPlayer?.player_id}
          leagueId={leagueId}
          isOpen={!!profileModalPlayer}
          onClose={() => setProfileModalPlayer(null)}
          userTeamId={teamHook.myTeam?.team_id}
        />
      )}
    </div>
  );
};

const LiveDraftWithErrorBoundary = () => (
  <DraftErrorBoundary>
    <LiveDraft />
  </DraftErrorBoundary>
);

export default LiveDraftWithErrorBoundary;
